import { cn } from '@/lib/cn';

export interface PaginationProps {
  pageIndex: number;
  pageSize: number;
  totalRows: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  className?: string;
}

const PAGE_SIZE_OPTIONS = [10, 20, 25, 50, 100];

export function Pagination({
  pageIndex,
  pageSize,
  totalRows,
  onPageChange,
  onPageSizeChange,
  className,
}: PaginationProps) {
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const safePage = Math.min(pageIndex, totalPages);
  const canPrev = safePage > 1;
  const canNext = safePage < totalPages;

  const pageNumbers = buildPageNumbers(safePage, totalPages);

  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 px-1 py-3 text-[13px] text-[#666]',
        className,
      )}
    >
      <div className="flex items-center gap-2">
        <span>Rows per page:</span>
        <select
          value={pageSize}
          onChange={(e) => {
            onPageSizeChange(Number(e.target.value));
          }}
          className="rounded-md border border-[#ddd] min-w-[76px] bg-white py-1 pl-3 pr-9 text-[13px] focus:border-[#6B0F1A] focus:outline-none focus:ring-1 focus:ring-[#6B0F1A]"
          aria-label="Rows per page"
        >
          {PAGE_SIZE_OPTIONS.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-1">
        <span className="mr-3">
          Page {safePage} of {totalPages}
        </span>
        <NavButton
          disabled={!canPrev}
          onClick={() => onPageChange(safePage - 1)}
          label="Previous page"
        >
          &lsaquo;
        </NavButton>
        {pageNumbers.map((p, i) =>
          p === '...' ? (
            <span key={`ellipsis-${i}`} className="px-1 text-[#999]">
              &hellip;
            </span>
          ) : (
            <button
              key={p}
              onClick={() => onPageChange(p as number)}
              className={cn(
                'min-w-[32px] rounded-md px-2 py-1 text-[13px] font-medium transition-colors',
                p === safePage ? 'bg-[#6B0F1A] text-white' : 'text-[#666] hover:bg-[#f0f0f0]',
              )}
              aria-current={p === safePage ? 'page' : undefined}
              aria-label={`Page ${p}`}
            >
              {p}
            </button>
          ),
        )}
        <NavButton disabled={!canNext} onClick={() => onPageChange(safePage + 1)} label="Next page">
          &rsaquo;
        </NavButton>
      </div>
    </div>
  );
}

function NavButton({
  disabled,
  onClick,
  label,
  children,
}: {
  disabled: boolean;
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
      className={cn(
        'min-w-[32px] rounded-md px-2 py-1 text-lg font-medium transition-colors',
        disabled ? 'cursor-not-allowed text-[#ccc]' : 'text-[#666] hover:bg-[#f0f0f0]',
      )}
    >
      {children}
    </button>
  );
}

function buildPageNumbers(current: number, total: number): (number | '...')[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const pages: (number | '...')[] = [1];

  if (current > 3) {
    pages.push('...');
  }

  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  if (current < total - 2) {
    pages.push('...');
  }

  pages.push(total);
  return pages;
}
